import React, { useState, useEffect } from "react"
import { useTranslation } from "@zestia/gatsby-plugin-react-i18next"
import Divider from "@/components/Divider"
import { getCookieForRegion } from "@/utils/cookie-detection"

const WebinarSignupBanner = ({
  className = "",
  type = "default",
  title,
  kickerText,
  description,
  buttonText,
  buttonLink,
  webinarDates,
}) => {
  const { t } = useTranslation()
  const [region, setRegion] = useState(null)
  const isWhiteBg = type === "whiteBg"
  const hasThreeDates = type === "threeDates"
  const resolvedTitle = title || t("webinar-signup-banner.title")
  const resolvedDescription =
    description || t("webinar-signup-banner.description")
  const resolvedButtonText = buttonText || t("webinar-signup-banner.button")
  const resolvedButtonLink =
    buttonLink ||
    "https://us02web.zoom.us/webinar/register/WN_9Ctkz3WWSNWBxoYYTieBjA#/registration"
  const resolvedKickerText = kickerText ?? t("webinar-signup-banner.kicker")

  // Hardcoded webinar dates - times in GMT
  const webinarDatesUK = [
    "2026-08-06T16:00:00+00:00", // Aug 6, 2026 at 4:00 PM GMT
    "2026-08-20T16:00:00+00:00", // Aug 20, 2026 at 4:00 PM GMT
    "2026-09-03T16:00:00+00:00", // Sep 3, 2026 at 4:00 PM GMT
    "2026-09-17T16:00:00+00:00", // Sep 17, 2026 at 4:00 PM GMT
    "2026-10-08T21:00:00+00:00", // Oct 8, 2026 at 9:00 PM GMT
    "2026-10-22T16:00:00+00:00", // Oct 22, 2026 at 4:00 PM GMT
    "2026-11-05T20:00:00+00:00", // Nov 5, 2026 at 8:00 PM GMT
    "2026-11-19T16:00:00+00:00", // Nov 19, 2026 at 4:00 PM GMT
    "2026-12-03T20:00:00+00:00", // Dec 3, 2026 at 8:00 PM GMT
    "2026-12-17T16:00:00+00:00", // Dec 17, 2026 at 4:00 PM GMT
  ]

  // Get region from cookie on component mount
  useEffect(() => {
    const regionFromCookie = getCookieForRegion()
    setRegion(regionFromCookie || "US") // Default to US if no cookie
  }, [])

  const getFutureDates = () => {
    const now = new Date()
    const sourceDates =
      Array.isArray(webinarDates) && webinarDates.length > 0
        ? webinarDates
        : webinarDatesUK

    return sourceDates.map(date => new Date(date)).filter(date => date > now) // Only future dates
  }

  const futureDates = getFutureDates()
  const nearestDate = futureDates[0]
  const nextThreeDates = futureDates.slice(0, 3)

  const getDateParts = date => {
    if (!date || !region) return null

    // Get timezone and locale based on region
    const getLocaleAndTimezone = () => {
      switch (region) {
        case "GB":
          return { locale: "en-GB", timeZone: "UTC", customTimezone: "GMT" }
        case "US":
          return { locale: "en-US", timeZone: "America/New_York" }
        case "LA":
          return { locale: "es-ES", timeZone: "America/Mexico_City" }
        default:
          // EU and everything else defaults to GMT
          return { locale: "en-US", timeZone: "UTC", customTimezone: "GMT" }
      }
    }

    const { locale, timeZone, customTimezone } = getLocaleAndTimezone()

    // Get individual parts
    const month = date.toLocaleDateString(locale, {
      month: "short",
      timeZone,
    })

    const day = date.toLocaleDateString(locale, {
      day: "numeric",
      timeZone,
    })

    const timeString = date.toLocaleTimeString(locale, {
      hour: "numeric",
      minute: date.getMinutes() === 0 ? undefined : "2-digit",
      hour12: true,
      timeZone,
      timeZoneName: customTimezone ? undefined : "short",
    })

    // Clean up the time format
    let cleanTime = timeString
      .replace(/:\d+/, "") // Remove :00 minutes
      .replace(/\s?(AM|PM)/i, match => match.toLowerCase().replace(/\s/, "")) // Make am/pm lowercase and remove space
      .replace(/\s+/g, " ") // Clean up multiple spaces

    // Add custom timezone if specified
    if (customTimezone) {
      cleanTime = `${cleanTime.replace(/\s\w+$/, "")} ${customTimezone}`
    }

    return { month, day, cleanTime }
  }

  const formatDate = (date, isCompact = false) => {
    const parts = getDateParts(date)
    if (!parts) return null

    const { month, day, cleanTime } = parts

    return (
      <div
        className={`webinar-date-container${isCompact ? " webinar-date-container--compact" : ""}`}
      >
        <div
          className={`webinar-date${isCompact ? " webinar-date--compact" : ""}`}
        >
          <span className="webinar-date-month">
            <span className="mb-0 h4">{month}</span>
            <span className={`mb-0 ${isCompact ? "h2" : "h1"}`}>
              {day}
            </span>{" "}
          </span>
          <span className="mb-0 p">{cleanTime}</span>
        </div>
      </div>
    )
  }

  // Don't show banner if no upcoming dates
  if (!nearestDate) return null

  if (hasThreeDates) {
    return (
      <div className={`container-fluid row ${className}`}>
        <div className="webinar-signup webinar-signup--white-bg webinar-signup--three-dates">
          <div className="webinar-signup__inner">
            <div className="webinar-signup__left">
              <div className="">
                <h3>{resolvedTitle}</h3>
                <p>{resolvedDescription}</p>
              </div>
              {resolvedButtonLink && resolvedButtonText && (
                <a
                  href={resolvedButtonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn webinar-signup__button"
                >
                  {resolvedButtonText}
                </a>
              )}
            </div>
            <Divider variant="dashed" orientation="vertical" />
            <div className="webinar-signup__dates-panel">
              {resolvedKickerText && (
                <span className="heading-kicker heading-kicker--no-status">
                  {resolvedKickerText}
                </span>
              )}
              <div className="webinar-signup__dates-list">
                {nextThreeDates.map(date => (
                  <React.Fragment key={date.toISOString()}>
                    {formatDate(date, true)}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isWhiteBg) {
    return (
      <div className={`container-fluid row ${className}`}>
        <div className="webinar-signup webinar-signup--white-bg">
          <div className="webinar-signup__inner">
            <div className="webinar-signup__date">
              {formatDate(nearestDate)}
            </div>
            <div className="webinar-signup__content">
              {resolvedKickerText && (
                <span className="heading-kicker heading-kicker--live">
                  {resolvedKickerText}
                </span>
              )}
              <h3>{resolvedTitle}</h3>
              <p>{resolvedDescription}</p>
            </div>
            <Divider variant="dashed" orientation="vertical" />
            {resolvedButtonLink && resolvedButtonText && (
              <a
                href={resolvedButtonLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--lg webinar-signup__button"
              >
                {resolvedButtonText}
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`container-fluid row ${className}`}>
      <div className="webinar-signup">
        <div className="webinar-signup__date">{formatDate(nearestDate)}</div>
        <div className="webinar-signup__content">
          {resolvedKickerText && (
            <span className="heading-kicker heading-kicker--live">
              {resolvedKickerText}
            </span>
          )}
          <h3>{resolvedTitle}</h3>
          <p>{resolvedDescription}</p>
        </div>
        {resolvedButtonLink && resolvedButtonText && (
          <a
            href={resolvedButtonLink}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--tertiary btn--lg webinar-signup__button"
          >
            {resolvedButtonText}
          </a>
        )}
      </div>
    </div>
  )
}

export default WebinarSignupBanner
